import type { Objective } from '../api/missions';
import { ca } from '../i18n/ca';
import { MissionMap, type MissionMapProps } from './MissionMap';

/** A small map is a short one: the card sits in the side column. */
const HEIGHT = 240;

/** The one layer the card draws, so it gives nothing but the places the night was about. */
const OBJECTIVES_ONLY = {
  area: true,
  zones: false,
  markers: false,
  objectives: true,
  forces: false,
  compositions: false,
  civilians: false,
  intel: false,
  places: false,
};

/**
 * The area of operations of a night that is over, small, with each objective on its place
 * coloured by how it ended. It is drawn only on the ended page, where the platform already
 * hands the player every objective; the terrain picture comes from the panel and, when it is
 * not there, the pins stand on the dark square and the legend below says the same.
 */
export function OutcomeMiniMap({
  map,
  objectives,
  states,
}: {
  map: Pick<MissionMapProps, 'world' | 'worldSize' | 'area'>;
  objectives: Objective[];
  states: Map<string, { state: string }>;
}) {
  const placed = objectives.filter((o) => Array.isArray(o.position));
  if (placed.length === 0) return null;
  const results: Record<string, string> = {};
  const counts = { done: 0, failed: 0, other: 0 };
  placed.forEach((o) => {
    const state = states.get(o.id)?.state;
    if (state === 'done') {
      results[o.id] = 'done';
      counts.done += 1;
    } else if (state === 'failed') {
      results[o.id] = 'failed';
      counts.failed += 1;
    } else {
      results[o.id] = 'open';
      counts.other += 1;
    }
  });
  const t = ca.sessionPage;
  return (
    <article className="card outcome-map" aria-label={t.outcomeMapTitle}>
      <h2>{t.outcomeMapTitle}</h2>
      <MissionMap
        {...map}
        objectives={placed}
        results={results}
        layers={OBJECTIVES_ONLY}
        fit="area"
        height={HEIGHT}
      />
      <ul className="outcome-legend muted small" aria-label={t.outcomeMapLabel(map.world)}>
        {(['done', 'failed', 'other'] as const)
          .filter((k) => counts[k] > 0)
          .map((k) => (
            <li key={k}>
              <span className={`dot ${k}`} aria-hidden="true" />
              {t.outcomeLegend[k]} <span className="num">{counts[k]}</span>
            </li>
          ))}
      </ul>
    </article>
  );
}
